import React, { PropsWithChildren, useCallback, useEffect } from 'react';
import {
  AstralWalletStatusContext,
  useAstralWalletStatus,
} from './context/AstralWalletStatusContext';
import styles from '../AstralWalletTransactionStatusPage.module.scss';
import { PaymentFailed } from './PaymentFailed/PaymentFailed';
import { CheckoutFailed } from './CheckoutFailed/CheckoutFailed';
import {
  AstralWalletTransactionStatus,
  AstralWalletTransactionStatusResponseType,
} from 'plugins/CheckoutPlugin/types/CheckoutTypes';
import { astralWalletPostMessageToParent } from 'plugins/CheckoutPlugin/components/PaymentsMethod/AstralWallet/utils/astralWalletPostMessageToParent';
import { Loader } from '@components';
import { useCheckoutStore } from 'mycheck-core';
import { AstralWalletIFrameMessage } from 'plugins/CheckoutPlugin/components/PaymentsMethod/AstralWallet/constants';

interface AstralWalletStatusProps {
  retryAstralWalletStatusInterval: () => void;
  transactionStatusData: AstralWalletTransactionStatusResponseType;
  isLoading: boolean;
}

type AstralWalletStatusComponentProps = React.FC<
  PropsWithChildren<AstralWalletStatusProps>
> & {
  Loader: React.FC;
  PaymentFailed: React.FC;
  CheckoutFailed: React.FC;
};

export const AstralWalletStatus: AstralWalletStatusComponentProps = ({
  transactionStatusData,
  retryAstralWalletStatusInterval,
  isLoading,
  children,
}) => {
  const { order, payment } = transactionStatusData || {};

  const { status: orderStatus, id: orderId } = order || {};
  const {
    status: paymentStatus,
    id: paymentId,
    type: paymentType,
  } = payment || {};

  const isPaymentSuccess = [
    AstralWalletTransactionStatus.SUCCESS,
    AstralWalletTransactionStatus.AUTHORISED,
  ].includes(paymentStatus);

  const isOrderSuccess = orderStatus === AstralWalletTransactionStatus.SUCCESS;

  useEffect(() => {
    if (isPaymentSuccess && isOrderSuccess) {
      astralWalletPostMessageToParent({
        type: AstralWalletIFrameMessage.REDIRECT_TO_CONFIRM_PAGE,
        payload: orderId,
      });
    }
  }, [isOrderSuccess, isPaymentSuccess, orderId]);

  return (
    <AstralWalletStatusContext.Provider
      value={{
        paymentStatus,
        orderStatus,
        paymentType,
        isLoading,
        paymentId,
        retryAstralWalletStatusInterval,
      }}
    >
      <div className={styles.astralWalletStatusPageContainer}>{children}</div>
    </AstralWalletStatusContext.Provider>
  );
};

AstralWalletStatus.Loader = (() => {
  const { isLoading } = useAstralWalletStatus();
  const { astralWalletLoaderLabel } = useCheckoutStore();

  if (!isLoading) return null;

  return <Loader label={astralWalletLoaderLabel} withOverlay={false} />;
}) as React.FC;

AstralWalletStatus.PaymentFailed = (() => {
  const { paymentStatus, isLoading } = useAstralWalletStatus();

  const isPaymentFailed = [
    AstralWalletTransactionStatus.FAILED,
    AstralWalletTransactionStatus.ERROR,
  ].includes(paymentStatus);

  if (isPaymentFailed && !isLoading) {
    return <PaymentFailed />;
  }
}) as React.FC;

AstralWalletStatus.CheckoutFailed = (() => {
  const { orderStatus, isLoading, paymentType, paymentId } =
    useAstralWalletStatus();

  const isOrderFailed = [
    AstralWalletTransactionStatus.FAILED,
    AstralWalletTransactionStatus.ERROR,
  ].includes(orderStatus);

  if (isOrderFailed && !isLoading) {
    return <CheckoutFailed paymentType={paymentType} paymentId={paymentId} />;
  }
}) as React.FC;
